import { Component, OnInit, OnDestroy, Inject, ViewChild } from '@angular/core';
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogModule,
} from '@angular/material/dialog';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Observable, Subject, of } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  takeUntil,
  catchError,
} from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { CommonModule } from '@angular/common';
import { trigger, state, style, transition, animate } from '@angular/animations';

import { BusinessPartnerService } from '../../../business-partners/business-partner/business-partner.service';
import {
  BusinessPartnerWithRoutes,
  RouteInfo,
  SubRouteInfo,
  FrequencyInfo,
} from './interfaces/client-route-locator.interface';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';
import { MatBadgeModule } from '@angular/material/badge';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { ClientFrequencyDisplayComponent } from './client-frequency-display/client-frequency-display.component';
import { FrequencyUpdateEvent } from './client-frequency-display/client-frequency-display.interface';
import { AssignTerritoryModalComponent } from './assign-territory-modal/assign-territory-modal.component';
import { AssignTerritoryModalResult } from './assign-territory-modal/interfaces/assign-territory-modal.interface';
import { BusinessPartnerNavigationService } from 'app/shared/services/business-partner-navigation.service';

@Component({
  selector: 'app-client-route-locator',
  templateUrl: './client-route-locator.component.html',
  styleUrls: ['./client-route-locator.component.scss'],
  standalone: true,
  imports: [
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
    CommonModule,
    MatChipsModule,
    MatBadgeModule,
    MatTooltipModule,
    MatMenuModule,
    ClientFrequencyDisplayComponent,
  ],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({height: '0px', minHeight: '0'})),
      state('expanded', style({height: '*'})),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class ClientRouteLocatorComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private menuCloseTimeout: any;
  private readonly MAX_VISIBLE_TERRITORIES = 4;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  // Form controls
  searchControl = new FormControl('');

  // Data properties
  dataSource = new MatTableDataSource<BusinessPartnerWithRoutes>([]);

  // Pagination properties
  totalItems = 0;
  pageSize = 10;
  currentPage = 0;
  pageSizeOptions = [5, 10, 25, 50];

  // UI states
  isLoading = false;

  // Table configuration
  displayedColumns: string[] = ['expand', 'info', 'location', 'territorios', 'actions'];
  expandedElement: BusinessPartnerWithRoutes | null = null;
  
  // State preservation for expanded row
  private expandedElementId: string | null = null;

  constructor(
    private dialogRef: MatDialogRef<ClientRouteLocatorComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private businessPartnerService: BusinessPartnerService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private readonly bpNavigationService: BusinessPartnerNavigationService
  ) {}

  ngOnInit(): void {
    this.setupSearch();
    // No cargar clientes por defecto, esperar a que el usuario busque
    // this.loadClients();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.menuCloseTimeout) {
      clearTimeout(this.menuCloseTimeout);
    }
  }

  private setupSearch(): void {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        switchMap(searchTerm => {
          this.currentPage = 0;
          // Clear expanded state when searching since the client might not be in the search results
          this.clearExpandedState();
          return this.loadClients(searchTerm);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  private loadClients(searchTerm?: string): Observable<any> {
    this.isLoading = true;
    
    const observer = this.businessPartnerService.searchBusinessPartnersWithRoutes(
      searchTerm, 
      this.currentPage, 
      this.pageSize
    ).pipe(
      catchError(error => {
        this.showErrorMessage('Error al cargar clientes');
        return of({ 
          status: 'error', 
          statusCode: '500', 
          data: { 
            data: [], 
            pageInfo: [{ total: 0 }] 
          } 
        });
      }),
      takeUntil(this.destroy$)
    );

    observer.subscribe(response => {
      this.isLoading = false;
      
      if (response && response.data && response.data.data) {
        this.dataSource.data = response.data.data;
        
        // Restore expanded row state after data load
        this.restoreExpandedState();
        
        // Obtener el total de items desde pageInfo
        if (response.data.pageInfo && response.data.pageInfo.length > 0) {
          this.totalItems = response.data.pageInfo[0].total;
        } else {
          this.totalItems = response.data.data.length;
        }
      } else {
        this.dataSource.data = [];
        this.totalItems = 0;
        // Clear expanded state if no data
        this.expandedElement = null;
        this.expandedElementId = null;
      }
    });
    
    return observer;
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    // Clear expanded state when changing pages since the client might not be on the new page
    this.clearExpandedState();
    this.loadClients(this.searchControl.value || '');
  }

  getClientDisplayName(client: BusinessPartnerWithRoutes): string {
    const code = client.externalKeys?.code || client.id?.toString() || '';
    const name = client.commercialName || client.name || '';

    return code ? `${code} - ${name}` : name;
  }

  /**
   * Navegar al detalle del Business Partner en nueva pestaña
   */
  navigateToClient(client: BusinessPartnerWithRoutes): void {
    const navigationData = {
      id: client._id,
      code: client.externalKeys?.code,
      name: client.name,
      externalKeys: client.externalKeys
    };

    this.bpNavigationService.navigateToBusinessPartnerDetail(navigationData, {}, true);
  }

  getClientAddress(client: BusinessPartnerWithRoutes): string {
    if (!client.principalAddress) return 'Sin dirección';
    
    const parts = [];
    if (client.principalAddress.address) parts.push(client.principalAddress.address);
    if (client.principalAddress.locationThree?.name) parts.push(client.principalAddress.locationThree.name);
    if (client.principalAddress.locationTwo?.name) parts.push(client.principalAddress.locationTwo.name);
    
    return parts.join(', ') || 'Sin dirección';
  }

  getClientPhone(client: BusinessPartnerWithRoutes): string {
    return client.principalPhone?.phone || 'Sin teléfono';
  }

  getRouteNames(client: BusinessPartnerWithRoutes): string {
    const routes = client.principalAddress?.routes || [];
    if (routes.length === 0) return 'Sin rutas';
    
    return routes.map(route => {
      const code = route.externalKeys?.code;
      return code ? `${code} - ${route.name}` : route.name;
    }).join(', ');
  }

  toggleExpandRow(client: BusinessPartnerWithRoutes): void {
    this.expandedElement = this.expandedElement === client ? null : client;
    // Store the expanded element ID for state preservation
    this.expandedElementId = this.expandedElement ? this.expandedElement._id : null;
  }

  hasRoutes(client: BusinessPartnerWithRoutes): boolean {
    return (client.subRoutes && client.subRoutes.length > 0) || false;
  }

  getRoutesCount(client: BusinessPartnerWithRoutes): number {
    return client.subRoutes?.length || 0;
  }

  getRoutesWithFrequency(client: BusinessPartnerWithRoutes): number {
    if (!client.subRoutes || client.subRoutes.length === 0) return 0;
    
    return client.subRoutes.filter(subRoute => 
      subRoute.frequencies && subRoute.frequencies.length > 0
    ).length;
  }

  getRoutesWithoutFrequency(client: BusinessPartnerWithRoutes): number {
    if (!client.subRoutes || client.subRoutes.length === 0) return 0;
    
    return client.subRoutes.filter(subRoute => 
      !subRoute.frequencies || subRoute.frequencies.length === 0
    ).length;
  }

  hasFrequencies(subRoute: SubRouteInfo): boolean {
    return subRoute.frequencies !== undefined && 
           subRoute.frequencies !== null && 
           subRoute.frequencies.length > 0;
  }

  getTerritories(client: BusinessPartnerWithRoutes): RouteInfo[] {
    return client.routes || [];
  }

  getTerritoriesToDisplay(client: BusinessPartnerWithRoutes) {
    const territories = this.getTerritories(client);
    return {
      visible: territories.slice(0, this.MAX_VISIBLE_TERRITORIES),
      hasMore: territories.length > this.MAX_VISIBLE_TERRITORIES,
      moreCount: territories.length - this.MAX_VISIBLE_TERRITORIES,
      total: territories.length
    };
  }

  closeMenuWithDelay(trigger: MatMenuTrigger): void {
    this.menuCloseTimeout = setTimeout(() => {
      trigger.closeMenu();
    }, 100);
  }

  cancelMenuClose(): void {
    if (this.menuCloseTimeout) {
      clearTimeout(this.menuCloseTimeout);
      this.menuCloseTimeout = null;
    }
  }

  getTerritoriesDisplay(client: BusinessPartnerWithRoutes): string {
    const territories = this.getTerritories(client);
    if (territories.length === 0) return 'Sin territorios';
    
    return territories.map(territory => {
      const code = territory.externalKeys?.code;
      return code ? `${code} - ${territory.name}` : territory.name;
    }).join(', ');
  }

  getSubRouteNames(client: BusinessPartnerWithRoutes): string {
    if (!client.subRoutes || client.subRoutes.length === 0) return 'Sin rutas';
    
    return client.subRoutes.map(subRoute => {
      const functionName = subRoute.function?.name || '';
      return functionName ? `${subRoute.name} (${functionName})` : subRoute.name;
    }).join(', ');
  }

  getSubRouteByFunction(subRoute: SubRouteInfo): string {
    const functionName = subRoute.function?.name || '';
    return functionName ? `${subRoute.name} (${functionName})` : subRoute.name;
  }

  getFrequenciesForSubRoute(subRoute: SubRouteInfo): string[] {
    if (!subRoute.frequencies || subRoute.frequencies.length === 0) return [];
    return subRoute.frequencies.map(freq => this.formatFrequencyType(freq.type));
  }

  formatFrequencyType(type: string): string {
    // Mapeo de frecuencias de inglés a español
    const frequencyMap: { [key: string]: string } = {
      'Monday': 'L',
      'Tuesday': 'M', 
      'Wednesday': 'X',
      'Thursday': 'J',
      'Friday': 'V',
      'Saturday': 'S',
      'Sunday': 'D',
      // Días completos
      'MONDAY': 'L',
      'TUESDAY': 'M',
      'WEDNESDAY': 'X', 
      'THURSDAY': 'J',
      'FRIDAY': 'V',
      'SATURDAY': 'S',
      'SUNDAY': 'D',
      // Abreviaciones comunes
      'MON': 'L',
      'TUE': 'M',
      'WED': 'X',
      'THU': 'J', 
      'FRI': 'V',
      'SAT': 'S',
      'SUN': 'D',
      // Números de días (0=Domingo, 1=Lunes, etc.)
      '0': 'D',
      '1': 'L', 
      '2': 'M',
      '3': 'X',
      '4': 'J',
      '5': 'V',
      '6': 'S',
      // Frecuencias especiales
      'DAILY': 'Diario',
      'WEEKLY': 'Semanal',
      'BIWEEKLY': 'Quincenal',
      'MONTHLY': 'Mensual'
    };

    // Si el tipo viene en formato de días específicos, intentar convertir
    if (frequencyMap[type.toUpperCase()]) {
      return frequencyMap[type.toUpperCase()];
    }

    // Si ya está en español, devolverlo tal como está
    if (['L', 'M', 'X', 'J', 'V', 'S', 'D'].includes(type.toUpperCase())) {
      return type.toUpperCase();
    }

    // Si no se encuentra mapeo, devolver el tipo original
    return type;
  }

  getFormattedFrequenciesDisplay(subRoute: SubRouteInfo): string {
    if (!subRoute.frequencies || subRoute.frequencies.length === 0) return '-';
    
    // Calcular días únicos y contadores según el patrón del sistema
    const uniqueDays = new Set<number>();
    let weeklyCount = 0; // D(n) - frecuencias tipo 'W' (Weekly)
    let customCount = 0; // A(n) - frecuencias tipo 'C' (Custom)
    
    subRoute.frequencies.forEach(freq => {
      // Si tiene días específicos, agregarlos
      if (freq.days && freq.days.length > 0) {
        freq.days.forEach(day => uniqueDays.add(day));
      }
      
      // Contar por tipo de frecuencia
      if (freq.type === 'W') {
        weeklyCount++;
      } else if (freq.type === 'C') {
        customCount++;
      }
    });
    
    // Convertir días a formato español (0=D, 1=L, 2=M, etc.)
    const dayMap = ['D', 'L', 'M', 'X', 'J', 'V', 'S'];
    const dayLetters = Array.from(uniqueDays)
      .sort()
      .map(day => dayMap[day] || day.toString())
      .join('');
    
    // Construir el formato final: "VI D(2) A(1)"
    let result = dayLetters || '';
    
    if (weeklyCount > 0 || customCount > 0) {
      const parts = [];
      if (weeklyCount > 0) parts.push(`D(${weeklyCount})`);
      if (customCount > 0) parts.push(`A(${customCount})`);
      
      if (result && parts.length > 0) {
        result += ' ' + parts.join(' ');
      } else if (parts.length > 0) {
        result = parts.join(' ');
      }
    }
    
    return result || '-';
  }

  getTotalFrequencies(client: BusinessPartnerWithRoutes): number {
    if (!client.subRoutes) return 0;
    
    return client.subRoutes.reduce((total, subRoute) => {
      return total + (subRoute.frequencies?.length || 0);
    }, 0);
  }


  clearSearch(): void {
    this.searchControl.setValue('');
    this.currentPage = 0;
    // Clear expanded state when clearing search
    this.clearExpandedState();
    this.loadClients();
  }

  openAssignTerritoryDialog(client: BusinessPartnerWithRoutes): void {
    const dialogRef = this.dialog.open(AssignTerritoryModalComponent, {
      width: '95%',
      maxWidth: '1200px',
      height: '95%',
      maxHeight: '800px',
      data: { clientId: client._id },
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result: AssignTerritoryModalResult) => {
      if (result?.success) {
        this.showSuccessMessage(
          `${result.territoriesAssigned} territorio(s) asignado(s) correctamente`
        );
        this.refreshClientData();
      }
    });
  }

  onClose(): void {
    this.dialogRef.close();
  }

  private showErrorMessage(message: string): void {
    this.snackBar.open(message, 'Cerrar', {
      duration: 5000,
      verticalPosition: 'top',
      panelClass: ['error-snackbar'],
    });
  }

  private showSuccessMessage(message: string): void {
    this.snackBar.open(message, 'Cerrar', {
      duration: 3000,
      verticalPosition: 'top',
      panelClass: ['success-snackbar'],
    });
  }

  private showInfoMessage(message: string): void {
    this.snackBar.open(message, 'Cerrar', {
      duration: 3000,
      verticalPosition: 'top',
      panelClass: ['info-snackbar'],
    });
  }

  // Event handlers for frequency updates
  onFrequencyUpdated(event: FrequencyUpdateEvent): void {
    // Optionally show a message
    this.showSuccessMessage(`Frecuencia ${event.action === 'updated' ? 'actualizada' : event.action === 'created' ? 'creada' : 'eliminada'} correctamente`);
    // Refresh data
    this.refreshClientData();
  }

  onRefreshRequested(): void {
    this.refreshClientData();
  }

  private refreshClientData(): void {
    // Store current expanded element ID before refresh
    if (this.expandedElement) {
      this.expandedElementId = this.expandedElement._id;
    }
    
    // Refresh the client list to get updated frequency data
    this.loadClients(this.searchControl.value || '');
  }

  private restoreExpandedState(): void {
    // If we had an expanded element, try to find it in the new data and restore the expansion
    if (this.expandedElementId && this.dataSource.data.length > 0) {
      const clientToExpand = this.dataSource.data.find(
        (client: BusinessPartnerWithRoutes) => client._id === this.expandedElementId
      );
      
      if (clientToExpand && this.hasRoutes(clientToExpand)) {
        // Restore the expanded element
        this.expandedElement = clientToExpand;
      } else {
        // Client not found or no longer has routes, clear the expansion state
        this.expandedElement = null;
        this.expandedElementId = null;
      }
    }
  }

  private clearExpandedState(): void {
    this.expandedElement = null;
    this.expandedElementId = null;
  }
}
